import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, Phone, Menu, X } from 'lucide-react';
import { useCart } from '../contexts/CartContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Navbar = () => {
  const { getTotalItems } = useCart();
  const [settings, setSettings] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/public/settings`);
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const getWhatsAppNumber = () => {
    return settings?.footer?.whatsapp || '5543996963570';
  };

  const handleWhatsAppClick = () => {
    const phoneNumber = getWhatsAppNumber();
    const message = 'Olá! Gostaria de falar com um especialista da MULTICELL.';
    const url = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  return (
    <nav className="bg-[#0F0F10] border-b border-[#2A2A2D] sticky top-0 z-50 backdrop-blur-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group">
            {settings?.logo?.url ? (
              <img 
                src={settings.logo.url} 
                alt={settings.logo.alt || "MULTICELL"} 
                className="h-10 w-10 sm:h-12 sm:w-12 object-contain rounded-full border border-[#C9A24D] group-hover:border-[#E0B85C] transition-all glow-gold"
              />
            ) : (
              <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-full border border-[#C9A24D] bg-[#1C1C1F] flex items-center justify-center">
                <span className="text-[#C9A24D] font-bold text-sm sm:text-lg">MC</span>
              </div>
            )}
            <span className="text-xl sm:text-2xl font-bold text-cinzel tracking-wider text-metallic">
              MULTICELL
            </span>
          </Link>

          {/* Desktop Navigation Links */}
          <div className="hidden md:flex items-center space-x-8">
            <Link 
              to="/" 
              className="text-[#B5B5B5] hover:text-[#C9A24D] transition-colors font-medium uppercase text-sm tracking-wider"
            >
              Início
            </Link>
            <Link 
              to="/catalogo" 
              className="text-[#B5B5B5] hover:text-[#C9A24D] transition-colors font-medium uppercase text-sm tracking-wider"
            >
              Catálogo Premium
            </Link>
            
            {/* CTA WhatsApp na Navbar */}
            <button
              onClick={handleWhatsAppClick}
              className="bg-[#25D366] hover:bg-[#20BA5A] text-white px-4 py-2 rounded-lg font-bold uppercase text-xs tracking-wider transition-all duration-300 flex items-center gap-2 shadow-lg hover:shadow-[0_4px_15px_rgba(37,211,102,0.5)]"
            >
              <Phone className="h-4 w-4" />
              Falar com Especialista
            </button>
          </div>

          {/* Right Side Icons */}
          <div className="flex items-center space-x-4">
            {/* Cart */}
            <Link 
              to="/carrinho" 
              className="relative text-[#C9C9C9] hover:text-[#C9A24D] transition-colors group"
              data-testid="cart-icon"
            >
              <ShoppingCart className="h-6 w-6" />
              {getTotalItems() > 0 && (
                <span className="absolute -top-2 -right-2 bg-[#C9A24D] text-[#0F0F10] rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold">
                  {getTotalItems()}
                </span>
              )}
            </Link>
            
            {/* Mobile Menu Button */}
            <button 
              className="md:hidden text-[#C9C9C9] hover:text-[#C9A24D] transition-colors p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Menu"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-[#1C1C1F] border-t border-[#2A2A2D]">
          <div className="px-4 py-4 space-y-4">
            <Link 
              to="/" 
              className="block text-[#B5B5B5] hover:text-[#C9A24D] transition-colors font-medium uppercase text-sm tracking-wider py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              Início
            </Link>
            <Link 
              to="/catalogo" 
              className="block text-[#B5B5B5] hover:text-[#C9A24D] transition-colors font-medium uppercase text-sm tracking-wider py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              Catálogo Premium
            </Link>
            
            {/* CTA WhatsApp Mobile */}
            <button
              onClick={() => {
                handleWhatsAppClick();
                setMobileMenuOpen(false);
              }}
              className="w-full bg-[#25D366] hover:bg-[#20BA5A] text-white px-4 py-3 rounded-lg font-bold uppercase text-sm tracking-wider transition-all duration-300 flex items-center justify-center gap-2"
            >
              <Phone className="h-4 w-4" />
              Falar com Especialista
            </button>
          </div>
        </div>
      )}

      {/* Subtle Bottom Glow */}
      <div className="h-[1px] bg-gradient-to-r from-transparent via-[#C9A24D] to-transparent opacity-30"></div>
    </nav>
  );
};

export default Navbar;
